import React, { createContext, useContext, useReducer, useEffect } from 'react';
import useAxios from 'scripts/hooks/useAxios';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const GlobalStateContext = createContext();

const getInitialLanguage = () => {
    const supportedLanguages = [
        'en',
        'sv',
        // 'dk',
        // 'de',
        'no',
        'fi'
    ];

    try {
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage) {
            return savedLanguage;
        }

        const browserLanguage = navigator.language?.split('-')[0] || 'en';
        const selectedLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : 'en';

        localStorage.setItem('language', selectedLanguage);

        return selectedLanguage;
    } catch (error) {
        return 'en';
    }
};

const initialState = {
    language: getInitialLanguage(),
    availableCompanies: null,
    activeUserCompany: null,
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_LANGUAGE':
            localStorage.setItem('language', action.payload);
            return { ...state, language: action.payload };
        case 'SET_AVAILABLE_COMPANIES': {
            let activeUserCompany = null;
            if (action.payload && action.payload.length > 0) {
                try {
                    const savedActiveUserCompany = JSON.parse(localStorage.getItem('activeUserCompany'));
                    if (savedActiveUserCompany.contactId) {
                        const foundCompany = action.payload.find((company) => company.contactId === savedActiveUserCompany.contactId);
                        if (!foundCompany) {
                            activeUserCompany = action.payload[0];
                            localStorage.setItem('activeUserCompany', JSON.stringify(activeUserCompany));
                        } else {
                            activeUserCompany = foundCompany;
                        }
                    } else {
                        activeUserCompany = action.payload[0];
                    }
                    localStorage.setItem('activeUserCompany', JSON.stringify(activeUserCompany));
                } catch (error) {
                    activeUserCompany = action.payload[0];
                    localStorage.setItem('activeUserCompany', JSON.stringify(activeUserCompany));
                }
            }
            return { ...state, availableCompanies: action.payload, activeUserCompany: activeUserCompany || null };
        }
        case 'SET_ACTIVE_USER_COMPANY':
            localStorage.setItem('activeUserCompany', JSON.stringify(action.payload));
            return { ...state, activeUserCompany: action.payload };
        default:
            return state;
    }
};

export const GlobalStateProvider = ({ children }) => {
    const [globalState, globalDispatch] = useReducer(reducer, initialState);
    const { inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { callApi } = useAxios();

    useEffect(() => {
        if (isAuthenticated && inProgress === InteractionStatus.None && globalState.availableCompanies === null) {
            callApi({
                method: 'get',
                url: '/api/user/availableCompanies'
            }).then((res) => {
                globalDispatch({ type: 'SET_AVAILABLE_COMPANIES', payload: res.data });
            }).catch((err) => console.error(err)); // eslint-disable-line no-console
        }
    }, [inProgress, callApi, globalState.availableCompanies, isAuthenticated]);

    return (
        <GlobalStateContext.Provider value={{ globalState, globalDispatch }}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalState = () => {
    return useContext(GlobalStateContext);
};